import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInfiniteQuery, useQuery } from "react-query";
import { getRequest, postRequest, queries } from "../../react-query";
import { TriangleLeftIcon, TriangleRightIcon } from "@radix-ui/react-icons";
import { useNavigate } from "react-router-dom";
import { ReactPlayer, Toast } from "../../components";
import Hls from "hls.js";
import { STREAMING_TYPE } from "./streaming-enum";

interface Camera {
  id: string;
  name: string;
  preview_link: string;
  hls_link: string;
  dash_link: string;
  status: string;
}

interface selectProps {
  camera: Camera;
}

export const HlsCameraViewer = ({ setStreamingType, streamingType }: any) => {
  const navigate = useNavigate();

  // Constants and State
  let size = 5;
  const loopInterval = 3000;
  const previewImageFetchInterval = 3000;
  const [page, setPage] = useState<number>(1);
  const [select, setSelect] = useState<any>({
    camera: "",
  });
  const [loopEnabled, setLoopEnabled] = useState<boolean>(false);
  const [activeCameras, setActiveCameras] = useState<Camera[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const videoRef = useRef<any>(null);
  const canvasRef = useRef(null);
  const [segmentNumber, setSegmentNumber] = useState(null);

  const streaming =
    streamingType === STREAMING_TYPE.dash
      ? STREAMING_TYPE.hls
      : STREAMING_TYPE.dash;

  // Data Fetching
  const {
    data = {},
    isLoading,
    error,
  }: any = useQuery(
    [queries.camera, page, size],
    () => getRequest(`/camera?status=ACTIVE&page=${page}&pageSize=${size}`),
    {
      enabled: !!page,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  // Destructuring Data
  const { data: cameras, pagination } = data;
  const totalPages = useMemo(
    () => Math.ceil(pagination?.totalCount / pagination?.pageSize),
    [pagination]
  );

  // Event Handlers
  const handleNextPage = useCallback(
    () => setPage((prevPage) => prevPage + 1),
    []
  );
  const handlePrevPage = useCallback(
    () => setPage((prevPage) => prevPage - 1),
    []
  );
  const onSelectChange = useCallback(
    (value: Camera, name: string) => setSelect({ ...select, [name]: value }),
    []
  );

  const handleButtonClick = useCallback(() => {
    setLoopEnabled((prev) => !prev);
  }, []);

  const sendSnapshotToServer = async (blob: any, segmentNumber: any) => {
    try {
      const snapshotFileName = `${Date.now()}_${select.camera.id}.jpeg`;
      const formData = new FormData();
      formData.append("file", blob, snapshotFileName);
      formData.append("camera_detail_id", select.camera.id);
      formData.append("is_streaming", "true");
      formData.append("segment_uri", segmentNumber);
      formData.append("streaming_type", streamingType)
     

      const res = await postRequest({
        endPoint: "/pre-event",
        payload: formData,
      });

      if (res.id) {
        setIsOpen(true);
      }
    } catch (error) {
      console.error("Error uploading snapshot:", error);
    }
  };

  const recordPreview = useCallback(async () => {
    if (videoRef.current && canvasRef.current && Hls.isSupported()) {
      const player = videoRef.current;
      const canvas: any = canvasRef.current;
      const time = player.currentTime;
      // Draw the current frame of the video on the canvas
      canvas.width = player.videoWidth;
      canvas.height = player.videoHeight;
      canvas
        .getContext("2d")
        .drawImage(player, 0, 0, canvas.width, canvas.height);

      // Get the snapshot data from the canvas as a Blob object
      const snapshotBlob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg")
      );

      // console.log("segment number3======>", segmentNumber);
      // Trigger the download of the snapshot Blob
      await sendSnapshotToServer(snapshotBlob, segmentNumber);
    }
  }, [segmentNumber]);

  //Effects
  useEffect(() => {
    const hlsInstance = new Hls({
      defaultAudioCodec: "mp4a.40.2",
      startPosition: -1,  // Allow seeking within the buffer
    });

    const onFragmentChanged = (event: any, data: any) => {
      setSegmentNumber(data.frag.relurl);
      console.log("segment uri:", data.frag.relurl);
    };

    // Event listener for fragment loaded
    // const onFragmentLoaded = (event: any, data: any) => {
    //   setSegmentNumber(data.frag.sn);
    // };

    if (Hls.isSupported() && select.camera && videoRef.current !== null) {
      const url = select.camera.hls_link;
      const player = videoRef.current;

      if (url && player !== null) {
        try {
          hlsInstance.loadSource(url);
          hlsInstance.attachMedia(player);
          //hlsInstance.on(Hls.Events.FRAG_LOADED, onFragmentLoaded);
          hlsInstance.on(Hls.Events.FRAG_CHANGED, onFragmentChanged);
        } catch (error) {
          console.error("Error initializing HLS instance:", error);
        }
      }
    }

    return () => {
      if (hlsInstance) {
        console.log("Destroying HLS instance");
        // Clean up event listeners
        hlsInstance.detachMedia();
        hlsInstance.stopLoad();
        //hlsInstance.off(Hls.Events.FRAG_LOADED, onFragmentLoaded);
        hlsInstance.off(Hls.Events.FRAG_CHANGED, onFragmentChanged);
        hlsInstance.destroy();
      }
    };
  }, [select.camera, videoRef.current]);

  useEffect(() => {
    if (cameras) setActiveCameras(cameras);
  }, [cameras]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCameras(
        cameras?.map((camera: any) => ({
          ...camera,
          preview_link: `${camera.preview_link}?timestamp=${Date.now()}`,
        }))
      );
    }, previewImageFetchInterval);

    return () => clearInterval(interval);
  }, [cameras]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    if (loopEnabled && activeCameras) {
      intervalId = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % activeCameras?.length);
      }, loopInterval);
    }
    return () => clearInterval(intervalId);
  }, [loopEnabled, activeCameras]);

  useEffect(() => {
    if (cameras) {
      setSelect({ ...select, ["camera"]: cameras[currentIndex] });
    }
  }, [cameras, currentIndex]);

  // Rendering
  return (
    <div className="w-full h-full">
      <div className="w-full px-8 py-4 flex justify-between items-center">
        <div className="flex space-x-2 items-center">
          <button disabled={page === 1} onClick={handlePrevPage}>
            <TriangleLeftIcon
              className={`w-10 h-10 cursor-pointer ${
                page === 1 ? "text-gray-500" : "text-white"
              }`}
            />
          </button>

          <div className="flex space-x-4 items-center text-white">
            {Array.isArray(cameras) &&
              activeCameras?.map((obj: any) => {
                return (
                  <div
                    onClick={() => onSelectChange(obj, "camera")}
                    key={obj.id}
                    className={`w-32 h-32 cursor-pointer relative border ${
                      select.camera.id === obj.id
                        ? "border-green"
                        : "border-header-border"
                    } rounded`}
                  >
                    <img
                      src={obj.preview_link}
                      alt="preview"
                      className="w-full h-full rounded"
                    />

                    <div className="w-full absolute bottom-0 px-1 flex justify-between items-center text-white">
                      <p>{obj.name}</p>
                      {obj.status === "ACTIVE" && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          x="0px"
                          y="0px"
                          width="15"
                          height="15"
                          fill="red"
                          viewBox="0 0 50 50"
                        >
                          <path d="M 8.0136719 7.0292969 A 1.0001 1.0001 0 0 0 7.3222656 7.3222656 C 2.7995047 11.846068 -1.1842379e-15 18.102792 0 25 C 0 31.897208 2.7995047 38.153932 7.3222656 42.677734 A 1.0001 1.0001 0 0 0 8.7363281 42.677734 L 11.570312 39.84375 A 1.0001 1.0001 0 0 0 11.570312 38.429688 C 8.1286602 34.987084 6 30.242812 6 25 C 6 19.757188 8.1297921 15.013788 11.572266 11.572266 A 1.0001 1.0001 0 0 0 11.572266 10.158203 L 8.7363281 7.3222656 A 1.0001 1.0001 0 0 0 8.0136719 7.0292969 z M 41.957031 7.0292969 A 1.0001 1.0001 0 0 0 41.263672 7.3222656 L 38.427734 10.158203 A 1.0001 1.0001 0 0 0 38.427734 11.572266 C 41.870208 15.013788 44 19.757188 44 25 C 44 30.242812 41.870208 34.986212 38.427734 38.427734 A 1.0001 1.0001 0 0 0 38.427734 39.841797 L 41.263672 42.677734 A 1.0001 1.0001 0 0 0 42.677734 42.677734 C 47.201645 38.154865 50 31.897208 50 25 C 50 18.102792 47.200495 11.846068 42.677734 7.3222656 A 1.0001 1.0001 0 0 0 41.957031 7.0292969 z M 8.0976562 9.5117188 L 9.5195312 10.933594 C 6.1269359 14.664061 4 19.575176 4 25 C 4 30.424712 6.1260807 35.337173 9.5175781 39.068359 L 8.0976562 40.488281 C 4.3450168 36.394537 2 30.995061 2 25 C 2 19.004939 4.3450168 13.605463 8.0976562 9.5117188 z M 41.902344 9.5117188 C 45.654983 13.605463 48 19.004939 48 25 C 48 30.995061 45.655695 36.395442 41.902344 40.488281 L 40.480469 39.066406 C 43.873064 35.335939 46 30.424824 46 25 C 46 19.575176 43.873064 14.664061 40.480469 10.933594 L 41.902344 9.5117188 z M 14.382812 13.398438 A 1.0001 1.0001 0 0 0 13.691406 13.691406 C 10.796092 16.587786 9 20.593819 9 25 C 9 29.406181 10.796092 33.412214 13.691406 36.308594 A 1.0001 1.0001 0 0 0 15.105469 36.308594 L 17.931641 33.482422 A 1.0001 1.0001 0 0 0 17.931641 32.068359 C 16.119902 30.255711 15 27.761761 15 25 C 15 22.238239 16.119902 19.744289 17.931641 17.931641 A 1.0001 1.0001 0 0 0 17.931641 16.517578 L 15.105469 13.691406 A 1.0001 1.0001 0 0 0 14.382812 13.398438 z M 35.587891 13.398438 A 1.0001 1.0001 0 0 0 34.894531 13.691406 L 32.068359 16.517578 A 1.0001 1.0001 0 0 0 32.068359 17.931641 C 33.880098 19.744289 35 22.238239 35 25 C 35 27.761761 33.880098 30.255711 32.068359 32.068359 A 1.0001 1.0001 0 0 0 32.068359 33.482422 L 34.894531 36.308594 A 1.0001 1.0001 0 0 0 36.308594 36.308594 C 39.203908 33.412214 41 29.406181 41 25 C 41 20.593819 39.203908 16.587786 36.308594 13.691406 A 1.0001 1.0001 0 0 0 35.587891 13.398438 z M 14.466797 15.880859 L 15.947266 17.361328 C 14.184764 19.450917 13 22.061346 13 25 C 13 27.938654 14.184764 30.549083 15.947266 32.638672 L 14.466797 34.119141 C 12.335969 31.66133 11 28.50273 11 25 C 11 21.49727 12.335969 18.33867 14.466797 15.880859 z M 35.533203 15.880859 C 37.664031 18.33867 39 21.49727 39 25 C 39 28.50273 37.664031 31.66133 35.533203 34.119141 L 34.052734 32.638672 C 35.815236 30.549083 37 27.938654 37 25 C 37 22.061346 35.815236 19.450917 34.052734 17.361328 L 35.533203 15.880859 z M 25 18 C 21.134 18 18 21.134 18 25 C 18 28.866 21.134 32 25 32 C 28.866 32 32 28.866 32 25 C 32 21.134 28.866 18 25 18 z M 25 20 C 27.757 20 30 22.243 30 25 C 30 27.757 27.757 30 25 30 C 22.243 30 20 27.757 20 25 C 20 22.243 22.243 20 25 20 z"></path>
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>

          <button disabled={page === totalPages} onClick={handleNextPage}>
            <TriangleRightIcon
              className={`w-10 h-10 ${
                page === totalPages ? "text-gray-500" : "text-white"
              }`}
            />
          </button>
        </div>

        <div className="flex h-full flex-col space-y-2">
          <button
            disabled={true}
            onClick={() => setStreamingType(streaming)}
            className="bg-[#6E6767] p-1 rounded text-white capitalize"
          >
            Switch {streaming}
          </button>
          <button
            onClick={handleButtonClick}
            className={`p-1 rounded text-white ${
              loopEnabled ? "bg-blue-600" : "bg-[#6E6767]"
            }`}
          >
            {loopEnabled ? "Stop Looping" : "Start Looping"}
          </button>
          {select.camera && (
            <button
              onClick={() => navigate(`/${select.camera.id}/labelling`)}
              className="w-36 bg-blue-600 p-1 text-white"
            >
              Submit Label
            </button>
          )}
        </div>
      </div>

      {select.camera && (
        <div className="relative h-[calc(100vh-240px)] px-4">
          <ReactPlayer
            type="inbuilt"
            videoRef={videoRef}
            url={streaming ? select.camera.hash_link : select.camera.dash_link}
          />
          <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
          <div className="fixed bottom-24 right-8">
            <Toast open={isOpen} setOpen={setIsOpen}>
              <button
                onClick={recordPreview}
                className="bg-red-500 active:bg-red-800 px-6 py-1.5 text-white rounded-full"
              >
                Record
              </button>
            </Toast>
          </div>
        </div>
      )}
    </div>
  );
};
